import { useState } from 'react';
import AdminLayout from './AdminLayout';
import { T } from '@tolgee/react';

type Settings = {
  store: {
    name: string;
    domain: string;
    logoUrl: string;
  };
  payments: {
    bankTransferEnabled: boolean;
    iban: string;
    instructions: string;
  };
  email: {
    fromName: string;
    fromEmail: string;
    replyTo?: string;
  };
  search: {
    meiliHost: string;
  };
  ai: {
    qdrantUrl: string;
  };
};

const SettingsPage = () => {
  const [settings, setSettings] = useState<Settings>(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('admin_settings') : null;
    return stored
      ? JSON.parse(stored)
      : {
          store: { name: 'Jarad', domain: 'jarad.beeapp.es', logoUrl: '/Logo.svg' },
          payments: { bankTransferEnabled: true, iban: '', instructions: '' },
          email: { fromName: 'Jarad', fromEmail: 'no-reply@jaradpetfood.com', replyTo: '' },
          search: { meiliHost: '' },
          ai: { qdrantUrl: '' },
        } as Settings;
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const update = (path: string, value: any) => {
    setSettings((prev) => {
      const clone: any = { ...prev };
      const parts = path.split('.');
      let cur = clone;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return clone as Settings;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      // Persistencia local por ahora; la integración con backend puede añadirse después
      localStorage.setItem('admin_settings', JSON.stringify(settings));
      setSaved(true);
    } finally {
      setSaving(false);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  return (
    <AdminLayout title="Configuración">
      <div className="space-y-8">
        {/* Tienda */}
        <section className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            <T keyName="admin.settings.store">Tienda</T>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <T keyName="admin.settings.store_name">Nombre</T>
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={settings.store.name}
                onChange={(e) => update('store.name', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <T keyName="admin.settings.store_domain">Dominio</T>
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={settings.store.domain}
                onChange={(e) => update('store.domain', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <T keyName="admin.settings.store_logo">Logo URL</T>
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={settings.store.logoUrl}
                onChange={(e) => update('store.logoUrl', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Pagos */}
        <section className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            <T keyName="admin.settings.payments">Pagos</T>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-3 flex items-center gap-3">
              <input
                id="bankTransferEnabled"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                checked={settings.payments.bankTransferEnabled}
                onChange={(e) => update('payments.bankTransferEnabled', e.target.checked)}
              />
              <label htmlFor="bankTransferEnabled" className="text-sm text-gray-700">
                <T keyName="admin.settings.bank_transfer_enabled">Habilitar transferencia bancaria</T>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <T keyName="admin.settings.iban">IBAN</T>
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="ES00 0000 0000 0000 0000 0000"
                value={settings.payments.iban}
                onChange={(e) => update('payments.iban', e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <T keyName="admin.settings.payment_instructions">Instrucciones para el cliente</T>
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows={3}
                placeholder="Incluye el concepto de pago, plazo y contacto"
                value={settings.payments.instructions}
                onChange={(e) => update('payments.instructions', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Email */}
        <section className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            <T keyName="admin.settings.email">Email</T>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <T keyName="admin.settings.email_from_name">Remitente (nombre)</T>
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={settings.email.fromName}
                onChange={(e) => update('email.fromName', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <T keyName="admin.settings.email_from_email">Remitente (email)</T>
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={settings.email.fromEmail}
                onChange={(e) => update('email.fromEmail', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <T keyName="admin.settings.email_reply_to">Responder a</T>
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={settings.email.replyTo || ''}
                onChange={(e) => update('email.replyTo', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Integraciones */}
        <section className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            <T keyName="admin.settings.integrations">Integraciones</T>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <T keyName="admin.settings.meilisearch_host">Meilisearch Host</T>
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="https://meili.example.com"
                value={settings.search.meiliHost}
                onChange={(e) => update('search.meiliHost', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <T keyName="admin.settings.qdrant_url">Qdrant URL</T>
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="https://qdrant.example.com"
                value={settings.ai.qdrantUrl}
                onChange={(e) => update('ai.qdrantUrl', e.target.value)}
              />
            </div>
          </div>
        </section>

        <div className="flex justify-end gap-3">
          {saved && (
            <div className="text-green-700 bg-green-100 px-3 py-1 rounded">
              <T keyName="admin.settings.saved">Guardado</T>
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-900 disabled:opacity-60"
          >
            {saving ? <T keyName="admin.settings.saving">Guardando...</T> : <T keyName="admin.settings.save_changes">Guardar cambios</T>}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SettingsPage;
